import { action, computed, observable, runInAction, toJS } from 'mobx';
import moment, { Moment } from 'moment';
import {
	BookingPrices,
	bookingsApi,
	PastBookingsData,
	PricesRequest,
	PricesResponseData,
	UpcomingBookingsDataStructure,
	TermsAndConditions,
} from 'AppClient/api/Bookings';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { find, get, isEmpty, isNil } from 'lodash-es';
import { RootStore } from 'AppClient/stores';
import * as converters from 'utils/converters';
import { isFlightItem, hasFlight } from 'utils/flightsUtils';
import { DATE_TIME_FORMAT } from 'app-constants';

export type BookingsStep = 'PRICE' | 'BOOK' | 'REGARDS' | 'EDIT';

const defaultPricingPayload: PricesRequest = {
	pickupDateTimeInLocationTimeZone: '',
	numberOfPassengers: 1,
	addressPoints: [],
	carTypeId: null,
};

interface TermsAndConditionsModalData {
	servicePartnerName: string;
	termsAndConditionsData: TermsAndConditions | null;
	serviceProviderCurrencySymbol: string;
}

export class NewBookingsStore {
	@observable private _staticMapSrc?: string;
	@observable private _bookingPriceId: number | null = null;
	@observable
	pricesRequestPayload: PricesRequest = {
		pickupDateTimeInLocationTimeZone: '',
		numberOfPassengers: 1,
		addressPoints: [],
		carTypeId: null,
	};
	@observable
	bookBookingData: PricesResponseData = {
		pickUpDateTime: '',
		tripDistanceInMeters: null,
		tripDurationInSeconds: null,
		bookingNumber: '',
		bookingId: null,
		customerCurrencySymbol: '',
		bookingPrices: [],
	};
	@observable private _step: BookingsStep = 'PRICE';
	@observable private fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private fetchStatusCreateUpdate: FetchStatus = FetchStatus.IDLE;
	@observable private _showCreateBookingModal: boolean = false;
	@observable private _previousBookingId: number | null = null;
	@observable private _isCreditCardValid?: boolean = true;
	@observable upcomingBookingData: UpcomingBookingsDataStructure | null = null;
	@observable private _isBookAgain: boolean = false;
	@observable isShownTermsAndConditionsModal: boolean = false;
	@observable isTermsAndConditionsAcceptModalType: boolean = false;
	@observable termsAndConditionsModalData: TermsAndConditionsModalData = {
		servicePartnerName: '',
		termsAndConditionsData: null,
		serviceProviderCurrencySymbol: '',
	};
	@observable confirmationPickUpTimeChanged: boolean = false;
	@observable finalPickUpDateTime = '';

	constructor(private rootStore: RootStore) {}

	@computed
	get step() {
		return this._step;
	}

	@computed
	get isRebooking() {
		return this._isBookAgain;
	}

	@computed
	get previousBookingId() {
		return this._previousBookingId;
	}

	@action.bound
	setPreviousBookingId(previousBookingId: number | null) {
		this._previousBookingId = previousBookingId;
	}

	@computed
	get isShownCreateBookingModal() {
		return this._showCreateBookingModal;
	}

	@action.bound
	toggleModal(visible?: boolean) {
		const { _showCreateBookingModal, step } = this;
		const modalVisible = visible !== undefined ? visible : !_showCreateBookingModal;
		// on close
		if (!modalVisible) {
			this._isBookAgain = false;
			this.rootStore.passengerDetailsStore.clearStore();
			this.rootStore.bookingCCStore.clearBookingCreditCards();
			this.revokeObjectUrl();
			this.upcomingBookingData = null;
			if (step === 'REGARDS') {
				this.rootStore.historyStore.redirectTo('/');
			}
		}
		this._showCreateBookingModal = modalVisible;
	}

	@computed
	get pickupDateTime(): Moment | undefined {
		const { pickupDateTimeInLocationTimeZone } = this.pricesRequestPayload!;
		if (pickupDateTimeInLocationTimeZone) {
			const pickupDateTime = moment(pickupDateTimeInLocationTimeZone, DATE_TIME_FORMAT);

			if (pickupDateTime.isValid()) {
				return pickupDateTime;
			}
		}
		return undefined;
	}

	@computed
	get staticMapSrc() {
		return this._staticMapSrc;
	}

	@computed
	get isLoading() {
		return this.fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get isLoadingCreateUpdate() {
		return this.fetchStatusCreateUpdate === FetchStatus.PENDING;
	}

	@computed
	get isCreditCardValid() {
		return this._isCreditCardValid;
	}

	@computed
	get isPricingValid() {
		const { pricesRequestPayload } = this;
		const { isValidAddresses } = this.rootStore.addressesStore;
		const { isPassengerRatioValid } = this.rootStore.bookingDictionariesStore;
		const data = toJS(pricesRequestPayload);
		const isNotValid = Object.keys(data).some(key => {
			const value = data[key];
			if (typeof value === 'number') {
				return false;
			}
			return isEmpty(value);
		});
		return !isNotValid && isValidAddresses && isPassengerRatioValid;
	}

	@computed
	get bookingPriceId() {
		return this._bookingPriceId;
	}

	@computed
	get bookedSummary() {
		const {
			bookingPriceId,
			pricesRequestPayload: { numberOfPassengers },
			bookBookingData: {
				bookingPrices,
				customerCurrencySymbol,
				bookingNumber,
				pickUpDateTime,
			},
		} = this;
		if (bookingPriceId) {
			const bookingPrice = find<BookingPrices>(bookingPrices, {
				bookingPriceId,
			});
			if (bookingPrice) {
				return {
					getFullTripEstimate: this.getFullTripEstimate,
					customerCurrencySymbol,
					bookingPrice,
					bookingNumber,
					numberOfPassengers,
					pickUpDateTime: converters.datetimeWithTimeZone(pickUpDateTime),
				};
			}
		}
		return null;
	}

	@computed
	get getFullTripEstimate() {
		const { tripDistanceInMeters, tripDurationInSeconds } = this.bookBookingData;
		if (tripDistanceInMeters && tripDurationInSeconds) {
			const tripEstimate = converters.metersToMiles(tripDistanceInMeters);
			const { value, preferSuffix } = converters.secondsToMinutes(tripDurationInSeconds);
			return `Est. ${tripEstimate} mi, ${value} ${preferSuffix} ride`;
		}
		return '';
	}

	@action.bound
	setStep(step: BookingsStep) {
		this._step = step;
	}

	@action.bound
	setBookingPriceId(bookingPriceId: number | null) {
		this._bookingPriceId = bookingPriceId;
	}

	@action.bound
	acceptBookingById() {
		if (!isNil(this.bookingPriceId)) {
			this.bookOrUpdate();
		}
	}

	@action.bound
	updatePricesPayload(payload: Partial<PricesRequest>) {
		this.pricesRequestPayload = {
			...this.pricesRequestPayload,
			...payload,
		};
	}

	@action.bound
	async setPassengerData(bookingId: number | null) {
		try {
			const { createPassengerBooking } = this.rootStore.passengerDetailsStore;
			if (!bookingId) {
				// we can have bookingId === 0 in some exception cases on BE
				// when we haven't to fetch passenger data and route map
				return true;
			}
			const passengerResponse = await createPassengerBooking();
			if (passengerResponse.data.error) {
				this.rootStore.alertStore.setErrorAlert(passengerResponse.data.error);
				this.fetchStatus = FetchStatus.FAILURE;
				return false;
			}
			return true;
		} catch (e) {
			this.rootStore.alertStore.setRequestError(e);
			return false;
		}
	}

	@action.bound
	async setCreditCard(bookingId: number | null) {
		const {
			oneTimeCardId,
			addCardToBooking,
			addOneTimeCardToBooking,
			isCreditCardUser,
		} = this.rootStore.bookingCCStore!;
		if (!bookingId || !isCreditCardUser) {
			// we can have bookingId === 0 in some exception cases on BE
			// when we haven't to fetch passenger data and route map
			return true;
		}

		const isSuccess = oneTimeCardId
			? await addOneTimeCardToBooking(bookingId)
			: await addCardToBooking(bookingId);

		return isSuccess;
	}

	@action.bound
	async createPrices() {
		this.fetchStatus = FetchStatus.PENDING;
		this.revokeObjectUrl();
		try {
			const response = await bookingsApi.getPrice({
				...this.pricesRequestPayload,
			});
			runInAction(async () => {
				this.fetchStatus = FetchStatus.SUCCESS;
				const { result, error } = response.data;
				if (error) {
					this.rootStore.alertStore.setErrorAlert(error);
					this.fetchStatus = FetchStatus.FAILURE;
					return;
				}
				if (result !== null) {
					this.bookBookingData = result;
					const isPassengerDataUpdated = await this.setPassengerData(result.bookingId);
					const isCCUpdated = await this.setCreditCard(result.bookingId);
					if (isPassengerDataUpdated && isCCUpdated) {
						this.setStep('BOOK');
					}
				} else {
					this.fetchStatus = FetchStatus.FAILURE;
				}
			});
		} catch (e) {
			this.fetchStatus = FetchStatus.FAILURE;
			this.rootStore.alertStore.setRequestError(e);
		}
	}

	@computed
	get isEditingUpcomingBooking(): boolean {
		return this.previousBookingId !== null;
	}

	@action.bound
	async bookOrUpdate() {
		if (this.isEditingUpcomingBooking) {
			this.updateBooking();
		} else {
			this.createBooking();
		}
	}

	@action.bound
	async updateBooking() {
		if (this.bookingPriceId === null) {
			return;
		}
		if (this.isEditingUpcomingBooking) {
			try {
				this.fetchStatusCreateUpdate = FetchStatus.PENDING;
				const response = await bookingsApi.updateBookings({
					previousBookingId: this.previousBookingId!,
					currentBookingPriceId: this.bookingPriceId,
				});
				const { error, result } = response.data;
				if (error) {
					this.fetchStatusCreateUpdate = FetchStatus.FAILURE;
					this.rootStore.alertStore.setErrorAlert(error);
				} else {
					runInAction(() => {
						if (result) {
							this.finalPickUpDateTime = result.pickUpDateTime;
						}
						this.fetchStatusCreateUpdate = FetchStatus.SUCCESS;
						this.setStep('REGARDS');
						this.rootStore.upcomingBookingsStore.receiveUpcomingBookings();
						this.setTermsAndConditionsModalVisibility(false);
					});
				}
			} catch (e) {
				this.fetchStatusCreateUpdate = FetchStatus.FAILURE;
				this.rootStore.alertStore.setRequestError(e);
			}
		}
	}

	@action.bound
	async createBooking() {
		if (this.bookingPriceId === null) {
			return;
		}
		this.fetchStatusCreateUpdate = FetchStatus.PENDING;
		try {
			const response = await bookingsApi.createBooking({
				bookingPriceId: this.bookingPriceId,
			});
			const { error, result } = response.data;
			if (error) {
				this.rootStore.alertStore.setErrorAlert(error);
				this.fetchStatusCreateUpdate = FetchStatus.FAILURE;
				return;
			}

			this.rootStore.upcomingBookingsStore.receiveUpcomingBookings();
			runInAction(() => {
				if (result) {
					this.finalPickUpDateTime = result.pickUpDateTime;
				}
				this.fetchStatusCreateUpdate = FetchStatus.SUCCESS;
				this.setStep('REGARDS');
				this.setTermsAndConditionsModalVisibility(false);
			});
		} catch (e) {
			this.fetchStatusCreateUpdate = FetchStatus.FAILURE;
			this.rootStore.alertStore.setRequestError(e);
		}
	}

	getCarTypeId = (): null | number => {
		const {
			userStore: { userInfo },
			bookingDictionariesStore: { carTypesDictionary },
		} = this.rootStore;
		const carTypeId = get(carTypesDictionary, '[0].carTypeId', null);
		const preferredCarTypeId = get(userInfo, 'preferredCarTypeId', null);
		return isNil(preferredCarTypeId) ? carTypeId : preferredCarTypeId;
	};

	@action.bound
	prepareStartedPayload() {
		if (!this.isEditingUpcomingBooking && !this._isBookAgain) {
			const date = moment().format(DATE_TIME_FORMAT);
			const carTypeId = this.getCarTypeId();
			this.updatePricesPayload({
				...defaultPricingPayload,
				pickupDateTimeInLocationTimeZone: date,
				carTypeId,
			});
		}
		this.rootStore.bookingCCStore!.getCreditCardsForBooking();
	}

	@action.bound
	bookAnotherJourney() {
		this.rootStore.addressesStore.clearAllAddressesData();
		this.rootStore.passengerDetailsStore.clearStore();
		this.setPreviousBookingId(null);
		this.prepareStartedPayload();
		this.setStep('PRICE');
		this.upcomingBookingData = null;
	}

	@action.bound
	clearBookingPricing() {
		this.bookAnotherJourney();
	}

	@action.bound
	backToEdit() {
		this.setStep('EDIT');
		this.setBookingPriceId(null);
	}

	mapUpcomingToEdit = (upcoming: UpcomingBookingsDataStructure): PricesRequest => {
		return {
			pickupDateTimeInLocationTimeZone: converters.dateTimeZone(upcoming.pickUpDateTime),
			carTypeId: upcoming.carTypeId,
			numberOfPassengers: upcoming.numberOfPassengers,
			addressPoints: this.rootStore.addressesStore.getAllAddressesPoints(),
		};
	};

	@action.bound
	async restoreUpcomingLocations(upcoming: UpcomingBookingsDataStructure) {
		const {
			setAddress,
			addAddressPoint,
			receiveGoogleAddresses,
		} = this.rootStore.addressesStore;
		let index = 0;
		for (const item of upcoming.bookingAddresses) {
			let resetFlight,
				flightId = null;
			const label = `bookingLocation-${index}`;

			if (item.flight) {
				// we should invoke receiveGoogleAddresses again
				// because of flight can be not actual
				// In this case we show location as simple lcoation.
				await receiveGoogleAddresses(item.flight.flightNumber, {
					label,
					date: converters.dateTimeZone(upcoming.pickUpDateTime),
					isPickup: index !== upcoming.bookingAddresses.length - 1,
				});
				const { addressesSuggestions } = this.rootStore.addressesStore;
				if (isEmpty(addressesSuggestions[label])) {
					resetFlight = true;
				} else {
					const location = addressesSuggestions[label][0];
					flightId = isFlightItem(location) ? location.id : null;
				}
			}

			setAddress({ ...item, flight: resetFlight ? null : item.flight }, label);

			addAddressPoint(index, {
				addressPlaceId: item.placeId,
				//flightId: item.flight && !resetFlight ? item.flight.id : null,
				flightId: item.flight && !resetFlight ? flightId : null,
			});
			index++;
		}
	}

	async setPreviousCreditCard(bookingId: number) {
		const { getCreditCardByBookingId, setBookingCardId } = this.rootStore.bookingCCStore;

		const previousCreditCard = await getCreditCardByBookingId(bookingId);
		if (previousCreditCard) {
			setBookingCardId(previousCreditCard.customerUserCreditCardId);
		}
	}

	@action.bound
	async editUpcomingTrip(bookingId: number) {
		const upcoming = this.rootStore.upcomingBookingsStore.findUpcomingById(bookingId);
		if (upcoming) {
			await this.rootStore.passengerDetailsStore.fetchPassengersDetails(bookingId);
			runInAction(async () => {
				await this.setPreviousCreditCard(bookingId);
				await this.restoreUpcomingLocations(upcoming);
				this.upcomingBookingData = upcoming;
				this.pricesRequestPayload = this.mapUpcomingToEdit(upcoming);
				this.setPreviousBookingId(bookingId);
				this.setStep('EDIT');
				this.toggleModal(true);
			});
		}
	}
	hasAnyFlight = (pastBooking: PastBookingsData) => {
		return pastBooking.bookingAddresses.some(item => hasFlight(item));
	};

	mapPastBookingToEdit = (pastBooking: PastBookingsData): PricesRequest => {
		return {
			pickupDateTimeInLocationTimeZone: this.hasAnyFlight(pastBooking)
				? ''
				: moment().format(DATE_TIME_FORMAT),
			carTypeId: pastBooking.carTypeId,
			numberOfPassengers: pastBooking.numberOfPassengers,
			addressPoints: this.rootStore.addressesStore.getAllAddressesPoints(),
		};
	};

	@action.bound
	restoreRebookingLocations = (pastBooking: PastBookingsData) => {
		const { addAddressPoint, resetSingleLocation, setAddress } = this.rootStore.addressesStore;
		let index = 0;

		for (const item of pastBooking.bookingAddresses) {
			const label = `bookingLocation-${index}`;
			if (item.flight) {
				resetSingleLocation(label, {
					placeId: item.placeId,
					fullAddress: item.flight.flightNumber,
					name: '',
					flight: null,
				});
			} else {
				setAddress(item, label);
			}

			addAddressPoint(index, {
				addressPlaceId: item.placeId,
				flightId: null,
			});
			index++;
		}
	};

	@action.bound
	async editPastBooking(bookingId: number) {
		// re-booking
		const pastBooking = this.rootStore.pastBookingsStore.findPastBookingById(bookingId);
		if (pastBooking) {
			this._isBookAgain = true;
			await this.setPreviousCreditCard(bookingId);
			await this.rootStore.passengerDetailsStore.fetchPassengersDetails(bookingId);
			runInAction(async () => {
				this.restoreRebookingLocations(pastBooking);

				this.pricesRequestPayload = this.mapPastBookingToEdit(pastBooking);
				this.setStep('EDIT');
				this.toggleModal(true);
			});
		}
	}

	revokeObjectUrl() {
		const { _staticMapSrc } = this;
		if (_staticMapSrc) {
			URL.revokeObjectURL(_staticMapSrc);
			this._staticMapSrc = undefined;
		}
	}

	@action.bound
	async receiveStaticMap() {
		const { bookingId } = this.bookBookingData;
		if (bookingId && this._staticMapSrc === undefined) {
			try {
				const response = await bookingsApi.getStaticMap({
					bookingId,
				});
				const data = response.data;

				if (data) {
					const blob = new Blob([data], { type: 'image/png' });
					this._staticMapSrc = URL.createObjectURL(blob);
				}
			} catch (error) {
				this.rootStore.alertStore.setRequestError(error);
			}
		}
	}

	@action.bound
	toggleTermsAndConditionsModal(bookingPriceId?: number) {
		const { bookingPrices } = this.bookBookingData;
		const bookingPrice = find<BookingPrices>(bookingPrices, {
			bookingPriceId,
		});

		this.setBookingPriceId(bookingPriceId || null);

		if (bookingPrice) {
			this.termsAndConditionsModalData = {
				termsAndConditionsData: bookingPrice.termsAndConditions || null,
				servicePartnerName: bookingPrice.serviceProviderName || '',
				serviceProviderCurrencySymbol: bookingPrice.serviceProviderCurrencySymbol || '',
			};
		}

		this.setTermsAndConditionsModalVisibility(!this.isShownTermsAndConditionsModal);
	}

	@action.bound
	setTermsAndConditionsModalVisibility(visibility: boolean) {
		this.isShownTermsAndConditionsModal = visibility;

		if (visibility) {
			this.confirmationPickUpTimeChanged = false;
		}
	}

	@action.bound
	setTermsAndConditionsAcceptModalType(acceptModalType: boolean) {
		this.isTermsAndConditionsAcceptModalType = !!acceptModalType;
	}

	@action.bound
	toggleConfirmationPickUpTimeChanged() {
		this.confirmationPickUpTimeChanged = !this.confirmationPickUpTimeChanged;
	}
}
